import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { getCryptoPriceAtDate } from '../coingecko';
import { buildInventoryMap } from '../inventoryManagement';
import { usePayouts } from './usePayouts';

export interface CustomTokenHolding {
  cryptoId: string;
  symbol: string;
  name: string;
  amount: number;
  costBasisValue: number; // montant × coût moyen d'acquisition en €
}

export interface FailedPriceHolding {
  cryptoId: string;
  symbol: string;
  name: string;
}

/**
 * Calcule la valeur totale du portefeuille en € à une date donnée (case 212 du formulaire 2086).
 *
 * - Les cryptos avec prix CoinGecko : valeur = quantité × prix de marché à la date
 * - Les tokens custom (custom-*) : exclus du calcul automatique, listés séparément
 *   avec leur valeur au coût d'acquisition pour information
 * - Les cryptos dont le prix n'a pas pu être récupéré (ex: 429 CoinGecko persistant même
 *   après retry + fallback LiveCoinWatch) : exclues du total et listées séparément, pour
 *   que l'utilisateur soit averti que la case 212 est potentiellement incomplète plutôt
 *   que de lui présenter silencieusement un montant sous-évalué.
 *
 * Algorithme :
 * 1. Récupère les transactions ≤ date depuis le cache React Query (usePayouts)
 * 2. Reconstruit l'inventaire à cette date (buildInventoryMap)
 * 3. Récupère le prix historique de chaque crypto "standard" (CoinGecko, cache 24h), par
 *    petits lots séquentiels pour ne pas déclencher le rate limiting
 * 4. Somme quantité × prix pour chaque crypto pricée avec succès
 * 5. Retourne séparément les tokens custom et les cryptos dont le prix a échoué
 */
export const usePortfolioValueAtDate = ({
  date,
  enabled = true,
}: {
  date: string;
  enabled?: boolean;
}) => {
  const { data: allPayouts } = usePayouts();
  const queryClient = useQueryClient();

  // Avancement de la récupération des prix (une requête par token détenu), pour afficher une
  // barre de progression plutôt qu'un simple « Récupération… ». Remis à zéro au début de chaque
  // `queryFn` ; sans intérêt hors chargement, donc l'UI ne l'affiche que pendant `isLoading`.
  const [priceProgress, setPriceProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });

  // Calcule les quantités détenues à la date donnée, séparées en deux groupes
  const { pricedHoldings, customHoldings } = useMemo(() => {
    if (!allPayouts || !date || !enabled) {
      return { pricedHoldings: [], customHoldings: [] };
    }

    const dateObj = new Date(date);
    const txsUpToDate = allPayouts.filter((tx) => new Date(tx.date) <= dateObj);
    const inventoryMap = buildInventoryMap(txsUpToDate);

    const priced: { cryptoId: string; symbol: string; name: string; amount: number }[] = [];
    const custom: CustomTokenHolding[] = [];

    Array.from(inventoryMap.values())
      .filter((inv) => inv.totalAmount > 0)
      .forEach((inv) => {
        const firstAcq = inv.acquisitions[0];
        const crypto = firstAcq?.crypto;

        if (inv.cryptoId.startsWith('custom-')) {
          custom.push({
            cryptoId: inv.cryptoId,
            symbol: crypto?.symbol ?? inv.cryptoId,
            name: crypto?.name ?? inv.cryptoId,
            amount: inv.totalAmount,
            costBasisValue: inv.totalAmount * inv.averageCostBasis,
          });
        } else {
          priced.push({
            cryptoId: inv.cryptoId,
            symbol: crypto?.symbol ?? inv.cryptoId,
            name: crypto?.name ?? inv.cryptoId,
            amount: inv.totalAmount,
          });
        }
      });

    return { pricedHoldings: priced, customHoldings: custom };
  }, [allPayouts, date, enabled]);

  const dateKey = date ? date.split('T')[0] : '';

  // Récupère le prix historique de chaque crypto "standard" à cette date. Le vrai espacement
  // des appels réseau est géré de façon centralisée et globale (toute l'app) dans coingecko.ts
  // (file d'attente séquentielle + retry/backoff sur 429) — ici on peut donc simplement lancer
  // tous les fetch "en parallèle" côté JS sans risquer de rafale, y compris vis-à-vis d'autres
  // hooks (ex: useCryptoPriceAtDate) qui interrogent CoinGecko en même temps.
  // Chaque prix individuel est mis en cache via la même queryKey que useCryptoPriceAtDate,
  // donc les deux hooks partagent leur cache et évitent les doublons de requêtes.
  const pricesQuery = useQuery({
    queryKey: [
      'portfolio-value-prices',
      pricedHoldings.map((h) => h.cryptoId).sort().join(','),
      dateKey,
    ],
    queryFn: async () => {
      const results: Record<string, number | null> = {};
      setPriceProgress({ done: 0, total: pricedHoldings.length });

      await Promise.all(
        pricedHoldings.map(async (h) => {
          const priceQueryKey = ['crypto-price-at-date', h.cryptoId, dateKey];
          const price = await queryClient.fetchQuery({
            queryKey: priceQueryKey,
            queryFn: () => getCryptoPriceAtDate(h.cryptoId, new Date(date)),
            staleTime: 24 * 60 * 60 * 1000,
          });
          // Un échec (null, ex: rate-limit CoinGecko) ne doit pas rester en cache comme un
          // résultat valide pendant 24h — sinon un « Recalculer » ultérieur sert indéfiniment
          // ce null au lieu de retenter le réseau, alors que le prix est en réalité disponible.
          if (price === null) {
            queryClient.removeQueries({ queryKey: priceQueryKey, exact: true });
          }
          results[h.cryptoId] = price;
          setPriceProgress((p) => ({ ...p, done: p.done + 1 }));
        })
      );

      return results;
    },
    enabled: enabled && !!date && pricedHoldings.length > 0,
    staleTime: 24 * 60 * 60 * 1000, // Prix historiques immuables
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });

  const isLoading = pricedHoldings.length > 0 && pricesQuery.isFetching;

  // Valeur totale des cryptos "standard" (prix de marché), et liste de celles en échec
  const { marketValue, failedHoldings } = useMemo(() => {
    if (pricedHoldings.length === 0) {
      return { marketValue: 0, failedHoldings: [] as FailedPriceHolding[] };
    }
    if (pricesQuery.isFetching || !pricesQuery.data) {
      return { marketValue: null, failedHoldings: [] as FailedPriceHolding[] };
    }

    let total = 0;
    const failed: FailedPriceHolding[] = [];
    pricedHoldings.forEach((h) => {
      const price = pricesQuery.data?.[h.cryptoId];
      if (price !== null && price !== undefined) {
        total += h.amount * price;
      } else {
        failed.push({ cryptoId: h.cryptoId, symbol: h.symbol, name: h.name });
      }
    });

    return { marketValue: total, failedHoldings: failed };
  }, [pricedHoldings, pricesQuery.data, pricesQuery.isFetching]);

  // Coût d'acquisition total des tokens custom
  const customCostBasisTotal = useMemo(
    () => customHoldings.reduce((sum, h) => sum + h.costBasisValue, 0),
    [customHoldings]
  );

  // Valeur 212 calculable (sans les customs, sans les échecs de prix)
  const totalValue = marketValue;

  // Ce que 212 serait si on incluait les customs au coût d'acquisition
  const totalValueWithCustomCostBasis =
    marketValue === null ? null : marketValue + customCostBasisTotal;

  return {
    /** Valeur 212 calculée (tokens pricés avec succès uniquement) */
    totalValue,
    /** Valeur 212 hypothétique avec les tokens custom au coût d'acquisition */
    totalValueWithCustomCostBasis,
    /** Tokens custom détectés, exclus du calcul automatique */
    customHoldings,
    /** Cryptos dont le prix n'a pas pu être récupéré (ex: 429 persistant) — 212 potentiellement incomplète */
    failedHoldings,
    isLoading,
    /** Avancement de la récupération des prix pendant `isLoading` — { done, total } */
    priceProgress,
    holdingsCount: pricedHoldings.length + customHoldings.length,
    /**
     * Force un vrai nouvel appel réseau, y compris quand la requête agrégée a déjà des
     * données fraîches en cache (staleTime 24h) — sans ça, un clic sur « Recalculer » après
     * un calcul automatique déjà exécuté (même partiellement en échec) ne relance jamais
     * `queryFn`, contrairement à un simple changement du flag `enabled`.
     */
    refetch: pricesQuery.refetch,
  };
};
